export const DEFAULT_GAUSSIAN_BUDGET = 200000;

/**
 * Resolves the number of Gaussians to retain from a source dataset.
 * Infinity explicitly disables the Gaussian budget.
 */
export function resolveGaussianBudget(
  sourceCount,
  requestedBudget = DEFAULT_GAUSSIAN_BUDGET,
) {
  if (!Number.isInteger(sourceCount) || sourceCount < 0) {
    throw new Error(`Invalid source Gaussian count: ${sourceCount}`);
  }

  if (requestedBudget === Infinity) {
    return sourceCount;
  }

  if (!Number.isFinite(requestedBudget) || requestedBudget < 0) {
    throw new Error(`Invalid Gaussian budget: ${requestedBudget}`);
  }

  return Math.min(sourceCount, Math.floor(requestedBudget));
}

/**
 * Creates a deterministic uniform sampler over a Gaussian dataset.
 * Sampling returns source indices in monotonically increasing order,
 * allowing loaders to decode large files sequentially.
 */
export function createUniformSampler(
  sourceCount,
  requestedBudget = DEFAULT_GAUSSIAN_BUDGET,
) {
  const outputCount = resolveGaussianBudget(sourceCount, requestedBudget);

  const sampled = outputCount < sourceCount;

  if (outputCount === 0) {
    return {
      sourceCount,
      outputCount,
      sampled,

      sourceIndexFor() {
        return -1;
      },
    };
  }

  if (!sampled) {
    return {
      sourceCount,
      outputCount,
      sampled: false,

      sourceIndexFor(outputIndex) {
        return isValidOutputIndex(outputIndex, outputCount) ? outputIndex : -1;
      },
    };
  }

  const step = sourceCount / outputCount;

  return {
    sourceCount,
    outputCount,
    sampled: true,

    sourceIndexFor(outputIndex) {
      if (!isValidOutputIndex(outputIndex, outputCount)) {
        return -1;
      }

      return Math.min(sourceCount - 1, Math.floor((outputIndex + 0.5) * step));
    },
  };
}

/**
 * Checks whether an output index belongs to the sampler's range.
 */
function isValidOutputIndex(outputIndex, outputCount) {
  return (
    Number.isInteger(outputIndex) &&
    outputIndex >= 0 &&
    outputIndex < outputCount
  );
}
